import React, { useCallback, useMemo, useState, useEffect } from "react"
import { ScrollView, Switch } from "~/ui/primitives"
import { useSafeAreaInsets } from "~/ui/safe-area"
import { useFormik } from "formik"
import { observer } from "mobx-react-lite"
import * as Yup from "yup"
import { useTheme } from "~/ui/styled"
import { AutoRedPacketBetSettingScreenProps } from "./type"
import useNavigation from "~hooks/useNavigation"
import { ColumnFlex, BetweenFlex, CenterFlex, Text, Button, GradientButton, IconFont } from "~components/El"
import { TextField } from "~components/InputField"
import { globalToast, globalSpinner } from "~utils"
import { useStores } from "~models"
import { scrollViewFillStyle } from "~/ui/scroll-layout"

type FormValues = {
  amount: string
  amountOptions: string[]
  count: string
  remarkOptions: string[]
  intervalMinutes: string
  enabled: boolean
}

const Schema = Yup.object().shape({
  amount: Yup.number().typeError("金额格式不正确").positive("金额必须大于 0").required("请输入金额"),
  amountOptions: Yup.array()
    .of(Yup.number().typeError("金额格式不正确").positive("金额必须大于 0"))
    .max(10, "最多 10 个金额选项"),
  count: Yup.number()
    .typeError("格式错误")
    .integer("必须是整数")
    .min(1, "红包数量为 1-100")
    .max(100, "红包数量为 1-100")
    .required("请输入红包数量"),
  remarkOptions: Yup.array().of(Yup.string().max(50, "最多 50 字")).max(20, "最多 20 个下注备注选项"),
  intervalMinutes: Yup.number()
    .typeError("格式错误")
    .integer("必须是整数")
    .min(1, "最少 1 分钟")
    .required("请输入发放时间间隔"),
  enabled: Yup.boolean().required(),
})

const AutoRedPacketBetSettingScreen: React.FC<AutoRedPacketBetSettingScreenProps> = (props) => {
  const { chat_id, user_id } = props?.route?.params || {}
  const { apiStore } = useStores()
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const navigation = useNavigation()
  const [loading, setLoading] = useState(false)

  const formik = useFormik<FormValues>({
    initialValues: {
      amount: "100",
      amountOptions: [],
      count: "2",
      remarkOptions: ["01", "02", "03"],
      intervalMinutes: "30",
      enabled: true,
    },
    validationSchema: Schema,
    validateOnChange: true,
    enableReinitialize: true,
    onSubmit: async (vals) => {
      if (loading) return
      setLoading(true)
      try {
        const amountOptions = [vals.amount, ...vals.amountOptions]
          .map((v) => String(v).trim())
          .filter((v) => v !== "")
          .map((v) => Number(v))
          .filter((v) => !Number.isNaN(v))

        const remarkOptions = vals.remarkOptions
          .map((v) => String(v).trim())
          .filter((v) => v !== "")

        const payload = {
          chat_id,
          user_id,
          amount_options: amountOptions,
          remark_options: remarkOptions,
          interval_minutes: Number(vals.intervalMinutes),
          enabled: vals.enabled,
        }
        // console.log("payload", payload)
        globalSpinner.show()
        const res: any = await apiStore.saveAutoSendRedpacketConfig(payload)
        if (res) {
          globalToast.show("保存成功")
          navigation.goBack()
        } else {
          globalToast.show(res?.message || res?.msg || "保存失败")
        }
      } catch (error) {
        globalToast.show((error as Error)?.message || "保存失败")
      } finally {
        globalSpinner.close()
        setLoading(false)
      }
    },
  })

  const getAutoSendRedpacketConfig = useCallback(async () => {
    try {
      globalSpinner.show()
      const res: any = await apiStore.getAutoSendRedpacketConfig({ chat_id, user_id })
      const data = res?.data ?? res?.result ?? res
      if (data && typeof data === "object") {
        const amountOpts: any[] = Array.isArray(data.amount_options) ? data.amount_options : []
        const [first, ...rest] = amountOpts
        void formik.setValues({
          amount: first !== undefined && first !== null ? String(first) : "",
          amountOptions: rest.map((v) => String(v ?? "")),
          count: data.count !== undefined && data.count !== null ? String(data.count) : "2",
          remarkOptions: Array.isArray(data.remark_options) && data.remark_options.length > 0
            ? data.remark_options.map((v: any) => String(v ?? ""))
            : [""],
          intervalMinutes:
            data.interval_minutes !== undefined && data.interval_minutes !== null
              ? String(data.interval_minutes)
              : "",
          enabled: Boolean(data.enabled),
        })
      }
    } catch (error) {
      globalToast.show((error as Error)?.message || "获取失败")
    } finally {
      globalSpinner.close()
    }
  }, [chat_id, user_id])

  useEffect(() => {
    getAutoSendRedpacketConfig()
  }, [getAutoSendRedpacketConfig])

  const { values, errors, touched, setFieldValue, handleSubmit } = formik

  const amountOptionsError = useMemo(() => {
    const e = errors.amountOptions
    return typeof e === "string" ? e : ""
  }, [errors.amountOptions])

  const remarkOptionsError = useMemo(() => {
    const e = errors.remarkOptions
    return typeof e === "string" ? e : ""
  }, [errors.remarkOptions])

  const addAmountOption = useCallback(() => {
    void setFieldValue("amountOptions", [...values.amountOptions, ""])
  }, [setFieldValue, values.amountOptions])

  const removeAmountOption = useCallback(
    (index: number) => {
      void setFieldValue(
        "amountOptions",
        values.amountOptions.filter((_, i) => i !== index),
      )
    },
    [setFieldValue, values.amountOptions],
  )

  const addRemarkOption = useCallback(() => {
    void setFieldValue("remarkOptions", [...values.remarkOptions, ""])
  }, [setFieldValue, values.remarkOptions])

  const removeRemarkOption = useCallback(
    (index: number) => {
      void setFieldValue(
        "remarkOptions",
        values.remarkOptions.filter((_, i) => i !== index),
      )
    },
    [setFieldValue, values.remarkOptions],
  )

  return (
    <ScrollView
      style={scrollViewFillStyle}
      contentContainerStyle={{ paddingBottom: insets.bottom + 60, paddingHorizontal: 12 }}
      keyboardShouldPersistTaps="handled"
    >
      <ColumnFlex width="100%">
      <ColumnFlex width="100%" padding="12px" borderRadius="12px">
        <TextField
          label="红包金额（系统随机选择）"
          value={values.amount}
          placeholder="请输入金额"
          keyboardType="decimal-pad"
          suffix="元"
          onChangeText={(v) => void setFieldValue("amount", v)}
          error={touched.amount && typeof errors.amount === "string" ? errors.amount : ""}
        />

        {values.amountOptions.map((v, idx) => (
          <TextField
            key={`amountOption-${idx}`}
            value={v}
            placeholder="请输入金额"
            keyboardType="decimal-pad"
            suffix={
              <Button
                width="32px"
                height="32px"
                borderRadius="16px"
                bg="transparent"
                onClick={() => removeAmountOption(idx)}
              >
                <IconFont name="delete" color={theme.colors.error} size={16} />
              </Button>
            }
            onChangeText={(nv) =>
              void setFieldValue(
                "amountOptions",
                values.amountOptions.map((x, i) => (i === idx ? nv : x)),
              )
            }
            error={false}
            style={{ marginTop: 10 }}
          />
        ))}

        {amountOptionsError ? (
          <Text mt="6px" color={theme.colors.error} fontSize="12px">
            {amountOptionsError}
          </Text>
        ) : null}

        <Button
          width="100%"
          height="40px"
          mt="10px"
          borderRadius="8px"
          borderWidth="1px"
          borderColor={theme.colors.border}
          bg="transparent"
          onClick={addAmountOption}
          style={{ borderStyle: "dashed" }}
        >
          <CenterFlex>
            <Text color={theme.colors.secondary} fontSize="14px" fontWeight={600}>
              + 添加金额选项
            </Text>
          </CenterFlex>
        </Button>

        <TextField
          label="红包数量"
          value={values.count}
          placeholder="请输入红包数量"
          keyboardType="number-pad"
          editable={false}
          onChangeText={(v) => void setFieldValue("count", v)}
          error={touched.count && typeof errors.count === "string" ? errors.count : ""}
        />

        {values.remarkOptions.map((v, idx) => {
          return (
            <TextField
              key={`remarkOption-${idx}`}
              label={idx === 0 ? "下注备注（系统随机选择）" : ""}
              value={v}
              placeholder="请输入下注备注"
              suffix={
                values.remarkOptions.length > 1 ? (
                  <Button
                    width="32px"
                    height="32px"
                    borderRadius="16px"
                    bg="transparent"
                    onClick={() => removeRemarkOption(idx)}
                  >
                    <IconFont name="delete" color={theme.colors.error} size={16} />
                  </Button>
                ) : (
                  ""
                )
              }
              onChangeText={(nv) =>
                void setFieldValue(
                  "remarkOptions",
                  values.remarkOptions.map((x, i) => (i === idx ? nv : x)),
                )
              }
              error={idx === 0 && touched.remarkOptions && remarkOptionsError ? remarkOptionsError : false}
              style={idx === 0 ? undefined : { marginTop: 10 }}
            />
          )
        })}

        <Button
          width="100%"
          height="40px"
          mt="10px"
          borderRadius="8px"
          borderWidth="1px"
          borderColor={theme.colors.border}
          bg="transparent"
          onClick={addRemarkOption}
          style={{ borderStyle: "dashed" }}
        >
          <CenterFlex>
            <Text color={theme.colors.secondary} fontSize="14px" fontWeight={600}>
              + 添加备注选项
            </Text>
          </CenterFlex>
        </Button>

        <TextField
          label="发放时间间隔"
          value={values.intervalMinutes}
          placeholder="请输入时间间隔"
          keyboardType="number-pad"
          suffix="分钟"
          onChangeText={(v) => void setFieldValue("intervalMinutes", v)}
          error={
            touched.intervalMinutes && typeof errors.intervalMinutes === "string"
              ? errors.intervalMinutes
              : ""
          }
        />

        <BetweenFlex width="100%" mt="14px" padding="6px 0">
          <Text fontSize="14px" color={theme.colors.text} fontWeight={600}>
            启用自动发红包下注
          </Text>
          <Switch
            value={values.enabled}
            onValueChange={(v) => {
              void setFieldValue("enabled", v)
            }}
          />
        </BetweenFlex>
      </ColumnFlex>

      <BetweenFlex width="100%" padding="12px 0">
        <Button
          width="48%"
          height="44px"
          borderRadius="8px"
          bg={theme.colors.background}
          onClick={() => navigation.goBack()}
        >
          <Text color={theme.colors.infoText} fontSize={16}>
            取消
          </Text>
        </Button>
        <GradientButton
          width="48%"
          height={44}
          colors={['#fb994d', '#ee4b49']}
          borderRadius={8}
          loading={loading}
          disabled={loading}
          onClick={handleSubmit as any}
        >
          <Text color={theme.colors.textLight} fontSize={16}>
            保存设置
          </Text>
        </GradientButton>
      </BetweenFlex>
      </ColumnFlex>
    </ScrollView>
  )
}

export default observer(AutoRedPacketBetSettingScreen)

